import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, Check, Loader2 } from 'lucide-react';

import toast from 'react-hot-toast';

import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { auth } from '@/lib/firebase';
import { byocApi, ByocStatus } from '@/lib/api/byoc';
import { ByocConnectModal } from './byoc-connect-modal';

export function ByocCard() {
  const [status, setStatus] = useState<ByocStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  // Handle URL param to scroll to card and optionally open connect modal
  useEffect(() => {
    const shouldReconnect = searchParams.get('claude') === 'reconnect';
    if (!shouldReconnect || isLoading) return;

    // Scroll to card
    setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    // Open connect modal if not connected
    if (!status?.connected) {
      setTimeout(() => {
        setShowConnectModal(true);
      }, 300); // Small delay after scroll
    }

    // Clear the param
    searchParams.delete('claude');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, isLoading, status?.connected, setSearchParams]);

  const fetchStatus = async () => {
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const byocData = await byocApi.getStatus(token);
      setStatus(byocData);
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status !== 403) {
        console.error('Failed to fetch BYOC status:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (apiKey: string) => {
    const token = await auth.getIdToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    await byocApi.connect(token, apiKey);
    await fetchStatus();
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const result = await byocApi.disconnect(token);
      toast.success(result.message);
      await fetchStatus();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError?.response?.data?.message || 'Failed to disconnect');
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectDialog(false);
    }
  };

  return (
    <>
      <Card ref={cardRef} id="claude-account" className="border-neutral-200 dark:border-neutral-800 bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-medium">Bring Your Own Claude</h4>
                {isLoading ? (
                  <p className="text-xs text-muted-foreground">Loading...</p>
                ) : status?.connected ? (
                  <p className="text-xs text-muted-foreground">
                    Connected
                    {status.maskedKey && (
                      <span className="font-mono ml-1">
                        {status.maskedKey.slice(0, 10)}...{status.maskedKey.slice(-4)}
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Use your own subscription for unlimited usage
                  </p>
                )}
              </div>
            </div>
            {!isLoading && (
              status?.connected ? (
                <Button
                  variant="ghost"
                  onClick={() => setShowDisconnectDialog(true)}
                  className="shrink-0 text-xs h-7 px-2.5 text-muted-foreground hover:text-destructive"
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => setShowConnectModal(true)}
                  className="shrink-0 text-xs h-7 px-2.5 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                >
                  Connect
                </Button>
              )
            )}
          </div>
        </CardContent>
      </Card>

      <ByocConnectModal
        open={showConnectModal}
        onOpenChange={setShowConnectModal}
        onConnect={handleConnect}
        onSuccess={fetchStatus}
      />

      <AlertDialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Claude Account?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll use OptiDev credits for Claude usage until you reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnect} disabled={isDisconnecting}>
              {isDisconnecting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Disconnecting...</>
              ) : (
                'Disconnect'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
